import { computed, effect, Injectable, inject, signal } from '@angular/core';
import { Document } from '../model';
import { CanvasDocument, CanvasDocumentView, CanvasGroup, CanvasSnapshot } from './canvas.models';
import { CanvasStorageService } from './canvas-storage.service';

@Injectable({ providedIn: 'root' })
export class CanvasStateService {
  private readonly storage = inject(CanvasStorageService);
  private readonly storageKey = signal<string | null>(null);
  private readonly hydrated = signal(false);
  private readonly sourceDocuments = signal<Document[]>([]);
  private readonly groups = signal<CanvasGroup[]>([]);
  private readonly placements = signal<CanvasDocument[]>([]);

  readonly ready = computed(() => this.hydrated());
  readonly canvasDocuments = computed<CanvasDocumentView[]>(() => {
    const placements = this.placements();
    const docs = this.sourceDocuments();
    return placements.flatMap((placement) => {
      const document = docs.find((entry) => entry.id === placement.documentId);
      if (!document) {
        return [];
      }
      return [{
        ...document,
        documentId: placement.documentId,
        placementId: placement.id,
        x: placement.x,
        y: placement.y,
        groupId: placement.groupId ?? null,
      }];
    });
  });
  readonly groupsOnCanvas = computed(() => this.groups());
  readonly groupedDocumentIds = computed(() => {
    const grouped = new Map<string, string[]>();
    for (const placement of this.placements()) {
      if (!placement.groupId) {
        continue;
      }
      const ids = grouped.get(placement.groupId) ?? [];
      ids.push(placement.documentId);
      grouped.set(placement.groupId, ids);
    }
    return grouped;
  });
  readonly sidebarDocuments = computed(() => {
    const placedIds = new Set(this.placements().map((placement) => placement.documentId));
    return this.sourceDocuments().filter((document) => !placedIds.has(document.id));
  });

  constructor() {
    effect(() => {
      const key = this.storageKey();
      if (!key || !this.hydrated()) {
        return;
      }
      const snapshot = this.snapshot();
      this.storage.write(key, snapshot);
    });
  }

  setStorageKey(storageKey: string | null) {
    if (this.storageKey() === storageKey && this.hydrated()) {
      return;
    }
    this.storageKey.set(storageKey);
    this.hydrated.set(false);
    if (!storageKey) {
      this.groups.set([]);
      this.placements.set([]);
      this.sourceDocuments.set([]);
      return;
    }
    this.sourceDocuments.set([]);
    const snapshot = this.storage.read(storageKey);
    this.groups.set(snapshot?.groups ?? []);
    this.placements.set(snapshot?.documents ?? []);
    this.hydrated.set(true);
  }

  setSourceDocuments(documents: Document[], preservePlacements = false) {
    this.sourceDocuments.set(documents ?? []);
    if (!preservePlacements) {
      this.normalizePlacements();
    }
  }

  createGroup() {
    const group: CanvasGroup = {
      id: this.makeId('group'),
      title: `Group ${this.groups().length + 1}`,
      x: 48 + this.groups().length * 28,
      y: 48 + this.groups().length * 28,
      width: 360,
      height: 300,
    };
    this.groups.update((current) => [...current, group]);
  }

  createGroupForDocuments(documentIds: string[], x: number, y: number) {
    const group: CanvasGroup = {
      id: this.makeId('group'),
      title: `Group ${this.groups().length + 1}`,
      x,
      y,
      width: 360,
      height: 300,
    };
    const ids = new Set(documentIds);
    this.groups.update((current) => [...current, group]);
    this.placements.update((current) => {
      const placedIds = new Set<string>();
      const updated = current.map((placement) => {
        if (ids.has(placement.documentId) && !placement.groupId) {
          placedIds.add(placement.documentId);
          return { ...placement, groupId: group.id };
        }
        return placement;
      });
      for (const documentId of ids) {
        if (!placedIds.has(documentId)) {
          updated.push({ id: this.makeId('doc'), documentId, x, y, groupId: group.id });
        }
      }
      return updated;
    });
    return group;
  }

  renameGroup(groupId: string, title: string) {
    this.groups.update((current) => current.map((group) => (group.id === groupId ? {...group, title} : group)));
  }

  moveGroup(groupId: string, x: number, y: number) {
    this.groups.update((current) => current.map((group) => (group.id === groupId ? {...group, x, y} : group)));
  }

  addDocument(documentId: string, x: number, y: number, groupId?: string | null) {
    const normalizedGroupId = groupId ?? null;
    if (this.placements().some((placement) => placement.documentId === documentId && placement.groupId === normalizedGroupId)) {
      return;
    }
    this.placements.update((current) => [...current, {id: this.makeId('doc'), documentId, x, y, groupId: normalizedGroupId}]);
  }

  movePlacement(placementId: string, x: number, y: number) {
    this.placements.update((current) =>
      current.map((placement) => (placement.id === placementId ? {...placement, x, y} : placement))
    );
  }

  removePlacement(placementId: string) {
    this.placements.update((current) => current.filter((placement) => placement.id !== placementId));
  }

  setDocumentGroup(documentId: string, groupId: string | null) {
    if (groupId === null) {
      return;
    }
    const current = this.placements();
    const source = current.find((placement) => placement.documentId === documentId && !placement.groupId);
    if (source) {
      this.placements.set([
        ...current.filter((placement) => placement.id !== source.id),
        {...source, id: this.makeId('doc'), groupId},
      ]);
      return;
    }
    this.placements.update((list) => {
      if (list.some((placement) => placement.documentId === documentId && placement.groupId === groupId)) {
        return list;
      }
      return [...list, {id: this.makeId('doc'), documentId, x: 48, y: 48, groupId}];
    });
  }

  removeDocument(documentId: string) {
    this.placements.update((current) => current.filter((placement) => placement.documentId !== documentId));
  }

  deleteGroup(groupId: string) {
    this.groups.update((current) => current.filter((group) => group.id !== groupId));
    this.placements.update((current) => current.filter((placement) => placement.groupId !== groupId));
  }

  clear() {
    this.groups.set([]);
    this.placements.set([]);
    if (this.storageKey()) {
      this.storage.remove(this.storageKey()!);
    }
  }

  snapshot(): CanvasSnapshot {
    return {
      groups: this.groups(),
      documents: this.placements(),
    };
  }

  private normalizePlacements() {
    const validIds = new Set(this.sourceDocuments().map((document) => document.id));
    this.placements.update((current) => current.filter((placement) => validIds.has(placement.documentId)));
  }

  private makeId(prefix: string) {
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
